import { storableError } from '../util/errors';
import { sendSMSNotification, sendMMSNotification } from '../util/api';
import * as log from '../util/log';

// ================ Action types ================ //

export const SEND_SMS_NOTIFICATION_REQUEST = 'app/SmsNotifications/SEND_SMS_NOTIFICATION_REQUEST';
export const SEND_SMS_NOTIFICATION_SUCCESS = 'app/SmsNotifications/SEND_SMS_NOTIFICATION_SUCCESS';
export const SEND_SMS_NOTIFICATION_ERROR = 'app/SmsNotifications/SEND_SMS_NOTIFICATION_ERROR';

// ================ Reducer ================ //

const initialState = {
  sendSMSNotificationInProgress: false,
  sendSMSNotificationError: null,
};

const smsNotificationsReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEND_SMS_NOTIFICATION_REQUEST:
      return { ...state, sendSMSNotificationInProgress: true, sendSMSNotificationError: null };
    case SEND_SMS_NOTIFICATION_SUCCESS:
      return { ...state, sendSMSNotificationInProgress: false };
    case SEND_SMS_NOTIFICATION_ERROR:
      return { ...state, sendSMSNotificationInProgress: false, sendSMSNotificationError: payload };
    default:
      return state;
  }
};

export default smsNotificationsReducer;

// ================ Action creators ================ //

export const sendSMSNotificationRequest = () => ({ type: SEND_SMS_NOTIFICATION_REQUEST });
export const sendSMSNotificationSuccess = () => ({ type: SEND_SMS_NOTIFICATION_SUCCESS });
export const sendSMSNotificationError = e => ({ 
  type: SEND_SMS_NOTIFICATION_ERROR, 
  error: true, 
  payload: e 
});

// ================ Thunks ================ //

/**
 * Sends SMS notification to provider after successful lead request
 * @param {string} transactionId - The transaction ID to fetch details from
 * @param {Object} options - Additional options for SMS content
 * @param {string} options.marketplaceName - Name of the marketplace (default: 'marketplace')
 */
export const sendSMSNotificationForLeadRequest = (transactionId, options = {}) => (dispatch, getState, sdk) => {
  dispatch(sendSMSNotificationRequest());

  return sdk.transactions
    .show({ 
      id: transactionId, 
      include: ['listing', 'listing.author', 'customer'],
      'fields.user': ['profile.publicData']
    })
    .then(response => {
      console.log('Transaction response:', response);
      const transaction = response.data.data;
      const listing = response.data.included.find(item => item.type === 'listing');
      const provider = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'provider'
      );
      const customer = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'customer'
      );
      
      if (!provider?.id) {
        console.warn('No provider found for SMS notification');
        return Promise.resolve();
      }

      // Check if SMS notifications are enabled for the provider
      const providerPublicData = provider?.attributes?.profile?.publicData || {};
      
      // Check publicData, then default to disabled
      const smsNotificationsEnabled = 
        providerPublicData.smsNotifications !== undefined ? providerPublicData.smsNotifications :
        false; // Default to disabled if not specified
      
      if (!smsNotificationsEnabled) {
        console.log('SMS notifications disabled for provider:', provider.id.uuid);
        return Promise.resolve();
      }

      const { marketplaceName = 'marketplace' } = options;
      const customerName = customer?.attributes?.profile?.displayName || 'A customer';
      
      // Extract lead data from transaction protected data
      const protectedData = transaction.attributes?.protectedData || {};
      const leadStart = protectedData.leadStart;
      const leadEnd = protectedData.leadEnd;
      const serviceType = protectedData.serviceType;
      const location = protectedData.location;
      
      // Generate SMS text
      const smsText = generateLeadRequestSMSText({
        customerName,
        leadStart,
        leadEnd,
        listingTitle: listing?.attributes?.title || 'your listing',
        serviceType,
        location,
        marketplaceName,
      });

      const smsParams = {
        providerId: provider.id.uuid,
        smsText,
      };

      return sendSMSNotification(smsParams)
        .then(response => {
          dispatch(sendSMSNotificationSuccess());
          console.log('SMS notification sent successfully:', response);
          return response;
        })
        .catch(error => {
          dispatch(sendSMSNotificationError(storableError(error)));
          console.error('Failed to send SMS notification:', error);
          // Don't throw error - SMS failure shouldn't break the lead request flow
          return Promise.resolve();
        });
    })
    .catch(error => {
      dispatch(sendSMSNotificationError(storableError(error)));
      console.error('Failed to fetch transaction details for SMS notification:', error);
      return Promise.resolve();
    });
};

/**
 * Sends SMS notification to provider after successful inquiry
 * @param {string} transactionId - The transaction ID to fetch details from
 * @param {Object} options - Additional options for SMS content
 * @param {string} options.marketplaceName - Name of the marketplace (default: 'marketplace')
 */
export const sendSMSNotificationForInquiry = (transactionId, options = {}) => (dispatch, getState, sdk) => {
  dispatch(sendSMSNotificationRequest());

  return sdk.transactions
    .show({ 
      id: transactionId, 
      include: ['listing', 'listing.author', 'messages'],
      'fields.user': ['profile.publicData']
    })
    .then(response => {
      const transaction = response.data.data;
      const listing = response.data.included.find(item => item.type === 'listing');
      const provider = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'provider'
      );
      const messages = transaction.relationships?.messages?.data;
      
      if (!provider?.id) {
        console.warn('No provider found for SMS notification');
        return Promise.resolve();
      }

      // Check if SMS notifications are enabled for the provider
      const providerPublicData = provider?.attributes?.profile?.publicData || {};
      
      // Check publicData, then default to disabled
      const smsNotificationsEnabled = 
        providerPublicData.smsNotifications !== undefined ? providerPublicData.smsNotifications :
        false; // Default to disabled if not specified
      
      if (!smsNotificationsEnabled) {
        console.log('SMS notifications disabled for provider:', provider.id.uuid);
        return Promise.resolve();
      }

      const { marketplaceName = 'marketplace' } = options;
      
      // Get the first message content
      const message = messages && messages.length > 0 ? messages[0]?.attributes?.content : '';
      
      // Generate SMS text
      const smsText = generateInquirySMSText({
        listingTitle: listing?.attributes?.title || 'your listing',
        message,
        marketplaceName,
      });

      const smsParams = {
        providerId: provider.id.uuid,
        smsText,
      };

      return sendSMSNotification(smsParams)
        .then(response => {
          dispatch(sendSMSNotificationSuccess());
          console.log('SMS notification sent successfully:', response);
          return response;
        })
        .catch(error => {
          dispatch(sendSMSNotificationError(storableError(error)));
          console.error('Failed to send SMS notification:', error);
          // Don't throw error - SMS failure shouldn't break the inquiry flow
          return Promise.resolve();
        });
    })
    .catch(error => {
      dispatch(sendSMSNotificationError(storableError(error)));
      console.error('Failed to fetch transaction details for SMS notification:', error);
      return Promise.resolve();
    });
};

/**
 * Sends SMS notification to provider when a booking request is cancelled
 * @param {string} transactionId - The transaction ID to fetch details from
 * @param {Object} options - Additional options for SMS content
 * @param {string} options.marketplaceName - Name of the marketplace (default: 'marketplace')
 */
export const sendSMSNotificationForCancellation = (transactionId, options = {}) => (dispatch, getState, sdk) => {
  dispatch(sendSMSNotificationRequest());

  return sdk.transactions
    .show({ 
      id: transactionId, 
      include: ['listing', 'listing.author', 'customer'],
      'fields.user': ['profile.publicData']
    })
    .then(response => {
      console.log('Transaction response for cancellation:', response);
      const transaction = response.data.data;
      const listing = response.data.included.find(item => item.type === 'listing');
      const provider = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'provider'
      );
      const customer = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'customer'
      );
      
      if (!provider?.id) {
        console.warn('No provider found for SMS notification');
        return Promise.resolve();
      }

      // Check if SMS notifications are enabled for the provider
      const providerPublicData = provider?.attributes?.profile?.publicData || {};
      
      // Check publicData, then default to disabled
      const smsNotificationsEnabled = 
        providerPublicData.smsNotifications !== undefined ? providerPublicData.smsNotifications :
        false; // Default to disabled if not specified
      
      if (!smsNotificationsEnabled) {
        console.log('SMS notifications disabled for provider:', provider.id.uuid);
        return Promise.resolve();
      }

      const { marketplaceName = 'marketplace' } = options;
      const customerName = customer?.attributes?.profile?.displayName || 'A customer';
      
      // Extract lead data from transaction protected data
      const protectedData = transaction.attributes?.protectedData || {};
      const leadStart = protectedData.leadStart;
      const leadEnd = protectedData.leadEnd;
      const serviceType = protectedData.serviceType;
      const location = protectedData.location;
      
      // Generate SMS text for cancellation
      const smsText = generateCancellationSMSText({
        customerName,
        leadStart,
        leadEnd,
        listingTitle: listing?.attributes?.title || 'your listing',
        serviceType,
        location,
        marketplaceName,
      });

      const smsParams = {
        providerId: provider.id.uuid,
        smsText,
      };

      return sendSMSNotification(smsParams)
        .then(response => {
          dispatch(sendSMSNotificationSuccess());
          console.log('Cancellation SMS notification sent successfully:', response);
          return response;
        })
        .catch(error => {
          dispatch(sendSMSNotificationError(storableError(error)));
          console.error('Failed to send cancellation SMS notification:', error);
          // Don't throw error - SMS failure shouldn't break the cancellation flow
          return Promise.resolve();
        });
    })
    .catch(error => {
      dispatch(sendSMSNotificationError(storableError(error)));
      console.error('Failed to fetch transaction details for cancellation SMS notification:', error);
      return Promise.resolve();
    });
};

/**
 * Sends SMS notification to provider when a lead request is converted to a confirmed booking
 * @param {string} transactionId - The transaction ID to fetch details from
 * @param {Object} options - Additional options for SMS content
 * @param {string} options.marketplaceName - Name of the marketplace (default: 'marketplace')
 */
export const sendSMSNotificationForConfirmedBooking = (transactionId, options = {}) => (dispatch, getState, sdk) => {
  dispatch(sendSMSNotificationRequest());

  return sdk.transactions
    .show({ 
      id: transactionId, 
      include: ['listing', 'listing.author', 'customer'],
      'fields.user': ['profile.publicData']
    })
    .then(response => {
      console.log('Transaction response for confirmed booking:', response);
      const transaction = response.data.data;
      const listing = response.data.included.find(item => item.type === 'listing');
      const provider = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'provider'
      );
      const customer = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'customer'
      );
      
      if (!provider?.id) {
        console.warn('No provider found for SMS notification');
        return Promise.resolve();
      }

      // Check if SMS notifications are enabled for the provider
      const providerPublicData = provider?.attributes?.profile?.publicData || {};
      
      // Check publicData, then default to disabled
      const smsNotificationsEnabled = 
        providerPublicData.smsNotifications !== undefined ? providerPublicData.smsNotifications :
        false; // Default to disabled if not specified
      
      if (!smsNotificationsEnabled) {
        console.log('SMS notifications disabled for provider:', provider.id.uuid);
        return Promise.resolve();
      }

      const { marketplaceName = 'marketplace' } = options;
      const customerName = customer?.attributes?.profile?.displayName || 'A customer';
      
      // Extract lead data from transaction protected data
      const protectedData = transaction.attributes?.protectedData || {};
      const leadStart = protectedData.leadStart;
      const leadEnd = protectedData.leadEnd;
      const serviceType = protectedData.serviceType;
      const location = protectedData.location;
      
      // Generate SMS text for confirmed booking
      const smsText = generateConfirmedBookingSMSText({
        customerName,
        leadStart,
        leadEnd,
        listingTitle: listing?.attributes?.title || 'your listing',
        serviceType,
        location,
        marketplaceName,
      });

      const smsParams = {
        providerId: provider.id.uuid,
        smsText,
      };

      return sendSMSNotification(smsParams)
        .then(response => {
          dispatch(sendSMSNotificationSuccess());
          console.log('Confirmed booking SMS notification sent successfully:', response);
          return response;
        })
        .catch(error => {
          dispatch(sendSMSNotificationError(storableError(error)));
          console.error('Failed to send confirmed booking SMS notification:', error);
          // Don't throw error - SMS failure shouldn't break the booking flow
          return Promise.resolve();
        });
    })
    .catch(error => {
      dispatch(sendSMSNotificationError(storableError(error)));
      console.error('Failed to fetch transaction details for confirmed booking SMS notification:', error);
      return Promise.resolve();
    });
};

/**
 * Sends SMS/MMS notification when a new message is sent in a transaction
 * @param {string} transactionId - The transaction ID to fetch details from
 * @param {string} messageText - The plain text message content
 * @param {Object} imageData - Image data if message contains an image (optional)
 * @param {Object} options - Additional options for SMS content
 * @param {string} options.marketplaceName - Name of the marketplace (default: 'marketplace')
 */
export const sendSMSNotificationForNewMessage = (transactionId, messageText, imageData = null, options = {}) => (dispatch, getState, sdk) => {
  dispatch(sendSMSNotificationRequest());

  return sdk.transactions
    .show({ 
      id: transactionId, 
      include: ['listing', 'listing.author', 'customer', 'provider']
    }, { expand: true})
    .then(response => {
      const transaction = response.data.data;
      const listing = response.data.included.find(item => item.type === 'listing');
      const provider = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'provider'
      );
      const customer = response.data.included.find(item => 
        item.type === 'user' && 
        item.attributes?.profile?.publicData?.userType === 'customer'
      );
      
      // Determine who to send SMS to (the other party)
      const currentUser = getState().user.currentUser;
      const currentUserId = currentUser?.id?.uuid;
      
      let recipient;
      if (currentUserId === provider?.id?.uuid) {
        // Provider sent message, notify customer
        recipient = customer;
      } else if (currentUserId === customer?.id?.uuid) {
        // Customer sent message, notify provider
        recipient = provider;
      } else {
        console.warn('Could not determine message recipient for SMS notification');
        return Promise.resolve();
      }
      
      if (!recipient?.id) {
        console.warn('No recipient found for SMS notification');
        return Promise.resolve();
      }

      // Check if SMS notifications are enabled for the recipient
      const recipientPublicData = recipient?.attributes?.profile?.publicData || {};
      
      // Check publicData, then default to disabled
      const smsNotificationsEnabled = 
        recipientPublicData.smsNotifications !== undefined ? recipientPublicData.smsNotifications :
        false; // Default to disabled if not specified
      
      if (!smsNotificationsEnabled) {
        console.log('SMS notifications disabled for recipient:', recipient.id.uuid);
        return Promise.resolve();
      }

      const { marketplaceName = 'marketplace' } = options;
      const senderName = currentUserId === provider?.id?.uuid 
        ? (provider?.attributes?.profile?.displayName || 'Provider')
        : (customer?.attributes?.profile?.displayName || 'Customer');

      const isProvider = currentUserId === provider?.id?.uuid;
      
      // Determine if we should send MMS or SMS
      const hasImage = imageData && imageData.id;
      const imageUrl = imageData?.attributes?.variants?.default?.url || imageData?.url;
      
      if (hasImage && imageUrl) {
        // Send MMS with image
        const mmsText = generateNewMessageMMSText({
          senderName,
          listingTitle: listing?.attributes?.title || 'your listing',
          messageText: messageText || '',
          marketplaceName,
          isProvider,
        });

        const mmsParams = {
          providerId: recipient.id.uuid,
          mmsText,
          imageUrl: imageUrl,
          imageData: imageData
        };

        return sendMMSNotification(mmsParams)
          .then(response => {
            dispatch(sendSMSNotificationSuccess());
            console.log('New message MMS notification sent successfully:', response);
            return response;
          })
          .catch(error => {
            dispatch(sendSMSNotificationError(storableError(error)));
            console.error('Failed to send new message MMS notification:', error);
            // Don't throw error - MMS failure shouldn't break the message flow
            return Promise.resolve();
          });
      } else {
        // Send SMS without image
        const smsText = generateNewMessageSMSText({
          senderName,
          listingTitle: listing?.attributes?.title || 'your listing',
          messageText: messageText || '',
          marketplaceName,
          isProvider,
        });

        const smsParams = {
          providerId: recipient.id.uuid,
          smsText,
        };

        return sendSMSNotification(smsParams)
          .then(response => {
            dispatch(sendSMSNotificationSuccess());
            console.log('New message SMS notification sent successfully:', response);
            return response;
          })
          .catch(error => {
            dispatch(sendSMSNotificationError(storableError(error)));
            console.error('Failed to send new message SMS notification:', error);
            // Don't throw error - SMS failure shouldn't break the message flow
            return Promise.resolve();
          });
      }
    })
    .catch(error => {
      dispatch(sendSMSNotificationError(storableError(error)));
      console.error('Failed to fetch transaction details for new message SMS notification:', error);
      return Promise.resolve();
    });
};


// ================ Helper functions ================ //

/**
 * Generates SMS text for lead request notification
 */
const generateLeadRequestSMSText = ({
  customerName,
  listingTitle,
  leadStart,
  leadEnd,
  serviceType,
  location,
  marketplaceName,
}) => {
  const startDateStr = leadStart ? new Date(leadStart).toLocaleDateString() : 'N/A';
  const endDateStr = leadEnd ? new Date(leadEnd).toLocaleDateString() : 'N/A';
  const serviceTypeText = serviceType ? ` for ${serviceType}` : '';
  const locationText = location ? ` at ${location}` : '';
  
  let smsText = `📱 ${marketplaceName} Notification\n\n`;
  smsText += `🔔 New Lead Request\n\n`;
  smsText += `From: ${customerName}\n`;
  // smsText += `Listing: "${listingTitle}"\n`;
  smsText += `Dates: ${startDateStr} to ${endDateStr}\n`;
  if (serviceType) smsText += `Service: ${serviceType}\n`;
  if (location) smsText += `Location: ${location}\n`;
  smsText += `\nLog into your account to see full details and respond to the Pet Parent.`;
  
  return smsText;
};

/**
 * Generates SMS text for inquiry notification
 */
const generateInquirySMSText = ({
  listingTitle,
  message,
  marketplaceName,
}) => {
  const messagePreview = message && message.length > 60 
    ? `${message.substring(0, 60)}...` 
    : message || 'No message provided';
  
  let smsText = `📱 ${marketplaceName} Notification\n\n`;
  smsText += `❓ New Inquiry\n\n`;
  // smsText += `Listing: "${listingTitle}"\n\n`;
  smsText += `Message: "${messagePreview}"\n\n`;
  smsText += `Log into your account to see full details and respond to the Pet Parent.`;

  return smsText;
};

/**
 * Generates SMS text for cancellation notification
 */
const generateCancellationSMSText = ({
  customerName,
  listingTitle,
  leadStart,
  leadEnd,
  serviceType,
  location,
  marketplaceName,
}) => {
  const startDateStr = leadStart ? new Date(leadStart).toLocaleDateString() : 'N/A';
  const endDateStr = leadEnd ? new Date(leadEnd).toLocaleDateString() : 'N/A';
  const serviceTypeText = serviceType ? ` for ${serviceType}` : '';
  const locationText = location ? ` at ${location}` : '';
  
  let smsText = `📱 ${marketplaceName} Notification\n\n`;
  smsText += `❌ Booking Cancelled\n\n`;
  smsText += `By: ${customerName}\n`;
  // smsText += `Listing: "${listingTitle}"\n`;
  smsText += `Dates: ${startDateStr} to ${endDateStr}\n`;
  if (serviceType) smsText += `Service: ${serviceType}\n`;
  if (location) smsText += `Location: ${location}\n`;
  smsText += `\nLog into your account to see full details.`;
  
  return smsText;
};

/**
 * Generates SMS text for confirmed booking notification
 */
const generateConfirmedBookingSMSText = ({
  customerName,
  listingTitle,
  leadStart,
  leadEnd,
  serviceType,
  location,
  marketplaceName,
}) => {
  const startDateStr = leadStart ? new Date(leadStart).toLocaleDateString() : 'N/A';
  const endDateStr = leadEnd ? new Date(leadEnd).toLocaleDateString() : 'N/A';
  const serviceTypeText = serviceType ? ` for ${serviceType}` : '';
  const locationText = location ? ` at ${location}` : '';
  
  let smsText = `📱 ${marketplaceName} Notification\n\n`;
  smsText += `✅ Booking Confirmed\n\n`;
  smsText += `By: ${customerName}\n`;
  // smsText += `Listing: "${listingTitle}"\n`;
  smsText += `Dates: ${startDateStr} to ${endDateStr}\n`;
  if (serviceType) smsText += `Service: ${serviceType}\n`;
  if (location) smsText += `Location: ${location}\n`;
  smsText += `\nLog into your account to see full details and respond to the Pet Parent.`;

  return smsText;
};

/**
 * Generates SMS text for new message notification (text only)
 */
const generateNewMessageSMSText = ({
  senderName,
  listingTitle,
  messageText,
  marketplaceName,
  isProvider,
}) => {
  let messagePreview = '';
  
  if (messageText && messageText.trim()) {
    // Truncate message if too long
    messagePreview = messageText.length > 60 
      ? `${messageText.substring(0, 60)}...` 
      : messageText;
  }
  
  // Format the SMS with better structure
  let smsText = `📱 ${marketplaceName} Notification\n\n`;
  smsText += `New message from ${senderName}\n`;
  // smsText += `Listing: "${listingTitle}"\n\n`;
  
  if (messagePreview) {
    smsText += `Message: "${messagePreview}"\n\n`;
  }

  const respondToText = isProvider ? 'the Pet Parent' : 'your Pet Sitter';
  smsText += `Log into your account to see full details and respond to ${respondToText}.`;

  return smsText;
};

/**
 * Generates MMS text for new message notification with image
 */
const generateNewMessageMMSText = ({
  senderName,
  listingTitle,
  messageText,
  marketplaceName,
  isProvider,
}) => {
  let messagePreview = '';
  
  if (messageText && messageText.trim()) {
    // Truncate message if too long
    messagePreview = messageText.length > 60 
      ? `${messageText.substring(0, 60)}...` 
      : messageText;
  }
  
  // Format the MMS with better structure
  let mmsText = `📱 ${marketplaceName} Notification\n\n`;
  mmsText += `New message from ${senderName}\n`;
  // mmsText += `Listing: "${listingTitle}"\n\n`;
  
  if (messagePreview) {
    mmsText += `Message: "${messagePreview}"\n\n`;
  }

  const respondToText = isProvider ? 'the Pet Parent' : 'your Pet Sitter';
  
  mmsText += `📷 Image attached (see below)\n\n`;
  mmsText += `Log into your account to see full details and respond to ${respondToText}.`;
  
  return mmsText;
};
